/**
 * Makes every Vuetify VDialog draggable by its header area (top of the card).
 * Registered once globally, so individual dialogs need no changes.
 */

const HEADER_HEIGHT = 72;

const INTERACTIVE_SELECTOR =
  'input, textarea, select, button, a, label, [role="button"], [role="combobox"], [contenteditable="true"], .v-field, .v-selection-control, .v-tabs, .v-slider, .v-btn, .v-chip, .v-avatar';

const getDialogContent = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) {
    return null;
  }

  const content = target.closest<HTMLElement>('.v-dialog > .v-overlay__content');

  // Skip fullscreen dialogs and side drawers (VDialog styled as a full-height drawer)
  if (
    !content ||
    content.closest('.v-dialog--fullscreen, .setting-service-drawer') ||
    content.offsetHeight >= window.innerHeight - 1
  ) {
    return null;
  }

  return content;
};

const isHeaderZone = (content: HTMLElement, target: Element, clientY: number) => {
  if (target.closest(INTERACTIVE_SELECTOR)) {
    return false;
  }

  return clientY - content.getBoundingClientRect().top <= HEADER_HEIGHT;
};

export const setupDraggableDialogs = () => {
  const offsets = new WeakMap<HTMLElement, { x: number; y: number }>();

  const apply = (content: HTMLElement, x: number, y: number) => {
    offsets.set(content, { x, y });
    content.style.translate = `${x}px ${y}px`;
  };

  // Reset position when a dialog is (re)opened, not when it closes, so the
  // leave animation plays at the dragged position instead of jumping to center.
  const activeOverlays = new WeakSet<Element>();

  new MutationObserver(mutations => {
    for (const m of mutations) {
      const overlay = m.target as HTMLElement;

      if (!overlay.classList.contains('v-dialog')) {
        continue;
      }

      if (!overlay.classList.contains('v-overlay--active')) {
        activeOverlays.delete(overlay);
        continue;
      }

      if (activeOverlays.has(overlay)) {
        continue;
      }

      activeOverlays.add(overlay);

      const content = overlay.querySelector<HTMLElement>(':scope > .v-overlay__content');

      if (content && offsets.has(content)) {
        offsets.delete(content);
        content.style.translate = '';
      }
    }
  }).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });

  // Click on the scrim closes persistent dialogs by triggering their own close button,
  // so each dialog's reset/cleanup logic still runs.
  let pressedOnScrim = false;

  document.addEventListener('pointerdown', e => {
    pressedOnScrim = e.target instanceof Element && e.target.classList.contains('v-overlay__scrim');
  });

  document.addEventListener('click', e => {
    const target = e.target;

    if (!pressedOnScrim || !(target instanceof Element) || !target.classList.contains('v-overlay__scrim')) {
      return;
    }

    pressedOnScrim = false;

    const overlay = target.closest<HTMLElement>('.v-dialog');
    const content = overlay?.querySelector<HTMLElement>(':scope > .v-overlay__content');

    if (!content || content.closest('.v-dialog--fullscreen, .setting-service-drawer')) {
      return;
    }

    const closeBtn =
      content.querySelector<HTMLElement>('.v-dialog-close-btn') ??
      content.querySelector<HTMLElement>('button:has([class*="tabler-x"])') ??
      Array.from(content.querySelectorAll<HTMLElement>('button')).find(btn =>
        /^(cancel|close|no|dismiss)$/i.test(btn.textContent?.trim() ?? '')
      );

    closeBtn?.click();
  });

  document.addEventListener('pointermove', e => {
    if (e.buttons) {
      return;
    }

    const content = getDialogContent(e.target);
    const target = e.target as Element;

    if (content) {
      content.style.cursor = isHeaderZone(content, target, e.clientY) ? 'move' : '';
    }
  });

  document.addEventListener('pointerdown', e => {
    if (e.button !== 0) {
      return;
    }

    const content = getDialogContent(e.target);

    if (!content || !isHeaderZone(content, e.target as Element, e.clientY)) {
      return;
    }

    const start = offsets.get(content) ?? { x: 0, y: 0 };
    const startX = e.clientX;
    const startY = e.clientY;
    const rect = content.getBoundingClientRect();
    const margin = 40;

    // Keep at least part of the header reachable inside the viewport
    const minX = -rect.left - rect.width + margin + start.x;
    const maxX = window.innerWidth - rect.left - margin + start.x;
    const minY = -rect.top + start.y;
    const maxY = window.innerHeight - rect.top - margin + start.y;

    const onMove = (ev: PointerEvent) => {
      const x = Math.min(Math.max(start.x + ev.clientX - startX, minX), maxX);
      const y = Math.min(Math.max(start.y + ev.clientY - startY, minY), maxY);

      apply(content, x, y);
    };

    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  });
};
